import { FC, memo, useCallback, useMemo } from "react";
import type { CompanyFilters, Lookups, TreeLookupItem } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";
import { normalizeStringArray, normalizeMinMax } from "../utils/normalize";
import { renderFilterDetailList } from "../components/FilterDetailList";
import { summarizeSelectedTreeItems } from "../utils/summarizeTreeSelection";
import { ProductCategoryInput } from "@/components/filters/ProductCategory/ProductCategoryInput";
import CollectionSelect from "@/components/CollectionSelect/CollectionSelect";
import { FaiFilterSection } from "@/components/filters/UserFilters/FaiFilter/FaiFilterSection";
import { InstallAgeSection } from "@/components/filters/UserFilters/InstallAgeFilter/InstallAgeSection";

export type ProductCategoriesSlice = {
  ids: string[];
  departmentIds: string[];
  roleIds: string[];
  installAgeMonths?: { min?: number; max?: number };
};

export const defaultProductCategoriesSlice: ProductCategoriesSlice = {
  ids: [],
  departmentIds: [],
  roleIds: [],
  installAgeMonths: undefined,
};

const ProductCategoriesFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("productCategories");
  const { setSlice } = useCompanyFilterActions();

  const selectedProductCategories = slice.ids;
  const selectedFaiIds = useMemo(
    () => [...slice.departmentIds, ...slice.roleIds],
    [slice.departmentIds, slice.roleIds]
  );

  const handleProductCategorySelect = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) {
        setSlice("productCategories", defaultProductCategoriesSlice);
        return;
      }
      setSlice("productCategories", {
        ...slice,
        ids,
      });
    },
    [setSlice, slice]
  );

  const handleFaiSelect = useCallback(
    (selectedDepartmentIds: string[], selectedRoleIds: string[]) => {
      setSlice("productCategories", {
        ...slice,
        departmentIds: selectedDepartmentIds,
        roleIds: selectedRoleIds,
      });
    },
    [setSlice, slice]
  );

  const handleInstallAgeChange = useCallback(
    (installAge: { min?: number | null; max?: number | null }) => {
      setSlice("productCategories", {
        ...slice,
        installAgeMonths:
          installAge.min === undefined && installAge.max === undefined
            ? undefined
            : {
                min: installAge.min ?? undefined,
                max: installAge.max ?? undefined,
              },
      });
    },
    [setSlice, slice]
  );

  return (
    <div id="product-categories-filter-container">
      <ProductCategoryInput
        selectedItems={selectedProductCategories}
        onProductCategorySelect={handleProductCategorySelect}
        id="segments-product-category-input"
      />

      <CollectionSelect
        placeholder="Search Collections"
        type="product_category"
        onSelect={({ ids }) =>
          handleProductCategorySelect([
            ...selectedProductCategories,
            ...(ids as string[]).filter(
              (id) => !selectedProductCategories.includes(id)
            ),
          ])
        }
      />

      <FaiFilterSection
        disabled={selectedProductCategories.length === 0}
        selectedIds={selectedFaiIds}
        onFaiSelect={handleFaiSelect}
        id="segments-product-categories-fai-filter"
      />

      <InstallAgeSection
        filterHasSelection={selectedProductCategories.length > 0}
        disabled={selectedProductCategories.length === 0}
        value={slice.installAgeMonths || { min: undefined, max: undefined }}
        onChange={handleInstallAgeChange}
        sx={{ mb: 1 }}
      />
    </div>
  );
});

ProductCategoriesFilter.displayName = "ProductCategoriesFilter";

export const productCategoriesModule: FilterModule<
  ProductCategoriesSlice,
  Lookups
> = {
  key: "productCategories",
  defaultSlice: defaultProductCategoriesSlice,
  Component: ProductCategoriesFilter,
  getChips: (slice, setSlice, lookups) => {
    if (slice.ids.length === 0) return [];

    // Use summarization to get smart label and tooltip
    const categories = (lookups?.categories ?? []) as TreeLookupItem[];
    const { label, tooltipNames } = summarizeSelectedTreeItems(
      slice.ids,
      categories
    );

    // Build tooltip content from summarized names
    const tooltipContent =
      tooltipNames.length > 0
        ? renderFilterDetailList("Categories", tooltipNames)
        : undefined;

    return [
      {
        id: "productCategories",
        label: `Product Categories: ${label}`,
        filterKey: "productCategories" as const,
        onRemove: () =>
          setSlice({
            ids: [],
            departmentIds: [],
            roleIds: [],
            installAgeMonths: undefined,
          }),
        tooltipContent,
      },
    ];
  },
  getCount: (slice) => {
    return (
      slice.ids.length +
      slice.departmentIds.length +
      slice.roleIds.length +
      (slice.installAgeMonths ? 1 : 0)
    );
  },
  toCompanyFilters: (slice) => {
    if (slice.ids.length === 0) return {};

    const out: NonNullable<CompanyFilters["productCategories"]> = {
      ids: slice.ids,
    };
    if (slice.departmentIds.length > 0) {
      out.departmentIds = slice.departmentIds;
    }
    if (slice.roleIds.length > 0) {
      out.roleIds = slice.roleIds;
    }
    if (slice.installAgeMonths) {
      out.installAgeMonths = slice.installAgeMonths;
    }

    return { productCategories: out };
  },
  fromCompanyFilters: (filters) => {
    const pc = filters.productCategories;
    if (!pc) return defaultProductCategoriesSlice;

    return {
      ids: normalizeStringArray(pc.ids).map(String),
      departmentIds: normalizeStringArray(pc.departmentIds).map(String),
      roleIds: normalizeStringArray(pc.roleIds).map(String),
      installAgeMonths: normalizeMinMax(pc.installAgeMonths),
    };
  },
};
